import { useState, useEffect, useMemo, useCallback } from 'react';
import { LibraryFormData, LibraryTemplate, LibraryTemplateInput } from '../../types/LibraryTemplate';
import { LibraryApi, LibraryBatchItem } from '../../api/libraryApi';
import { LibraryConfig, libraryGrouping, pickFields } from '../../library/LibraryConfig';

/**
 * Return type for the useLibrary hook
 */
export interface UseLibraryReturn<F extends object> {
    // Template list state
    templates: LibraryTemplate<F>[];
    loading: boolean;
    error: string | null;

    // Selection state
    selectedTemplateId: string | null;
    selectedTemplate: LibraryTemplate<F> | null;
    selectTemplate: (id: string | null) => void;

    // Edit form state
    editForm: LibraryFormData<F>;
    setEditForm: React.Dispatch<React.SetStateAction<LibraryFormData<F>>>;
    saving: boolean;
    saveError: string | null;

    // Filtered/grouped data
    getFilteredTemplates: (searchTerm: string) => LibraryTemplate<F>[];
    /** Templates per group key of the library's grouping (base types unless the config says otherwise) */
    getTemplatesByGroup: (searchTerm: string) => Partial<Record<string, LibraryTemplate<F>[]>>;

    // CRUD operations
    loadTemplates: () => Promise<void>;
    saveTemplate: () => Promise<boolean>;
    deleteTemplate: () => Promise<boolean>;
    addTemplate: (data: LibraryFormData<F>) => Promise<string | null>;

    // Destructive bulk operation — removes every template. Irreversible.
    resetLibrary: () => Promise<number>;

    // Batch operations
    batchUpsertTemplates: (items: LibraryBatchItem<F>[]) => Promise<{ created: number; updated: number }>;

    // Destructive bulk operation — the library becomes exactly these templates. Irreversible.
    replaceAllTemplates: (items: Array<Pick<LibraryBatchItem<F>, 'id' | 'data'>>) => Promise<void>;
}

export function emptyLibraryForm<F extends object>(config: LibraryConfig<F>): LibraryFormData<F> {
    return { name: '', baseTypes: [], ...config.defaultFields };
}

function formFromTemplate<F extends object>(
    template: LibraryTemplate<F>,
    config: LibraryConfig<F>
): LibraryFormData<F> {
    return {
        name: template.translations.sv,
        baseTypes: template.baseTypes,
        ...config.defaultFields,
        ...pickFields<F>(template, config.fieldKeys),
    };
}

/**
 * Hook for managing one superadmin template library.
 * Encapsulates state management and CRUD operations; everything that is
 * specific to a library comes from its config.
 */
export function useLibrary<F extends object>(config: LibraryConfig<F>): UseLibraryReturn<F> {
    const api = useMemo(() => new LibraryApi<F>(config), [config]);

    // Template list state
    const [templates, setTemplates] = useState<LibraryTemplate<F>[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Selection state
    const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);

    // Edit form state
    const [editForm, setEditForm] = useState<LibraryFormData<F>>(() => emptyLibraryForm(config));
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);

    // Load templates
    const loadTemplates = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await api.getAllTemplates();
            setTemplates(data);
        } catch (err) {
            console.error('Failed to load library templates:', err);
            setError(config.labels.loadError);
        } finally {
            setLoading(false);
        }
    }, [api, config.labels.loadError]);

    // Initial load
    useEffect(() => {
        loadTemplates();
    }, [loadTemplates]);

    // Get selected template
    const selectedTemplate = useMemo(() => {
        return templates.find((t) => t.id === selectedTemplateId) || null;
    }, [templates, selectedTemplateId]);

    // Update edit form when selection changes
    useEffect(() => {
        if (selectedTemplate) {
            setEditForm(formFromTemplate(selectedTemplate, config));
            setSaveError(null);
        }
    }, [selectedTemplate, config]);

    // Select template
    const selectTemplate = useCallback((id: string | null) => {
        setSelectedTemplateId(id);
    }, []);

    // Filter templates by search term
    const getFilteredTemplates = useCallback(
        (searchTerm: string) => {
            if (!searchTerm.trim()) {
                return templates;
            }
            const term = searchTerm.toLowerCase();
            return templates.filter((t) => t.translations.sv.toLowerCase().includes(term));
        },
        [templates]
    );

    // Group templates by the library's grouping
    const { groupsOf } = useMemo(() => libraryGrouping(config), [config]);
    const getTemplatesByGroup = useCallback(
        (searchTerm: string): Partial<Record<string, LibraryTemplate<F>[]>> => {
            const filtered = getFilteredTemplates(searchTerm);
            const grouped: Partial<Record<string, LibraryTemplate<F>[]>> = {};

            filtered.forEach((template) => {
                groupsOf(template).forEach((groupKey) => {
                    (grouped[groupKey] ??= []).push(template);
                });
            });

            Object.values(grouped).forEach((list) => {
                list?.sort((a, b) => a.translations.sv.localeCompare(b.translations.sv, 'sv'));
            });

            return grouped;
        },
        [getFilteredTemplates, groupsOf]
    );

    // Save template (update)
    const saveTemplate = useCallback(async (): Promise<boolean> => {
        if (!selectedTemplateId) return false;

        setSaving(true);
        setSaveError(null);

        try {
            const fields = pickFields<F>(editForm, config.fieldKeys);
            // Partial<A & B> and A & Partial<B> are not unified for a generic F.
            const updates = {
                translations: { sv: editForm.name },
                baseTypes: editForm.baseTypes,
                ...fields,
            } as Partial<LibraryTemplateInput<F>>;
            await api.updateTemplate(selectedTemplateId, updates);

            // Optimistic update
            setTemplates((prev) =>
                prev.map((t) =>
                    t.id === selectedTemplateId
                        ? {
                              ...t,
                              translations: { ...t.translations, sv: editForm.name },
                              baseTypes: editForm.baseTypes,
                              ...fields,
                          }
                        : t
                )
            );
            return true;
        } catch (err) {
            console.error('Failed to save library template:', err);
            setSaveError(err instanceof Error ? err.message : 'Kunde inte spara mallen');
            return false;
        } finally {
            setSaving(false);
        }
    }, [api, config.fieldKeys, selectedTemplateId, editForm]);

    // Delete template
    const deleteTemplate = useCallback(async (): Promise<boolean> => {
        if (!selectedTemplateId) return false;

        setSaving(true);

        try {
            await api.deleteTemplate(selectedTemplateId);
            setTemplates((prev) => prev.filter((t) => t.id !== selectedTemplateId));
            setSelectedTemplateId(null);
            return true;
        } catch (err) {
            console.error('Failed to delete library template:', err);
            setSaveError(err instanceof Error ? err.message : 'Kunde inte ta bort mallen');
            return false;
        } finally {
            setSaving(false);
        }
    }, [api, selectedTemplateId]);

    // Add template (create)
    const addTemplate = useCallback(async (data: LibraryFormData<F>): Promise<string | null> => {
        try {
            const id = await api.createTemplate({
                translations: { sv: data.name },
                baseTypes: data.baseTypes,
                ...(pickFields<F>(data, config.fieldKeys) as F),
            });

            // Reload to get the full template with metadata
            await loadTemplates();
            return id;
        } catch (err) {
            console.error('Failed to add library template:', err);
            throw err;
        }
    }, [api, config.fieldKeys, loadTemplates]);

    // Reset library (delete every template). Irreversible.
    // Throws on failure so the caller can surface the error message.
    const resetLibrary = useCallback(async (): Promise<number> => {
        const count = await api.deleteAllTemplates();
        setTemplates([]);
        setSelectedTemplateId(null);
        return count;
    }, [api]);

    const batchUpsertTemplates = useCallback(
        (items: LibraryBatchItem<F>[]) => api.batchUpsertTemplates(items),
        [api]
    );

    const replaceAllTemplates = useCallback(
        (items: Array<Pick<LibraryBatchItem<F>, 'id' | 'data'>>) => api.replaceAllTemplates(items),
        [api]
    );

    return {
        templates,
        loading,
        error,

        selectedTemplateId,
        selectedTemplate,
        selectTemplate,

        editForm,
        setEditForm,
        saving,
        saveError,

        getFilteredTemplates,
        getTemplatesByGroup,

        loadTemplates,
        saveTemplate,
        deleteTemplate,
        addTemplate,
        resetLibrary,

        batchUpsertTemplates,
        replaceAllTemplates,
    };
}
